import { useEffect, useState } from 'react';

import { readNarrativeTopics } from '@api/narratives';
import { getTopicLibraryCardIcon } from '../../utilities/helpers';

import {
  LoaderSkeleton,
  toast,
  TextArea,
  TopicLibraryCard,
  truncateTextWithEllipsis,
} from '@utilities';

import styles from './_index.module.scss';

const NarrativesTopicsWindow = () => {
  const [hasFavoritedTopics, setHasFavoritedTopics] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [narrativeTopics, setNarrativeTopics] = useState([]);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    if (!isLoading) {
      return () => {
        controller.abort();
      };
    }

    const fetchNarrativeTopics = async () => {
      try {
        const topics = await readNarrativeTopics({ signal: controller.signal });
        if (topics.some((topic) => topic.isFavorited)) {
          setHasFavoritedTopics(true);
        }
        setNarrativeTopics(topics);
        setIsLoading(false);
      } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError') return;
        console.error('Error fetching narrative topics:', error);
        toast('Error loading topics. Please refresh the page or try again.', { status: 'error' });
        setIsLoading(false);
      }
    };

    fetchNarrativeTopics();

    return () => {
      controller.abort();
    };
  }, [isLoading]);

  const normalizedSearchValue = searchValue.trim().toLowerCase();

  const filteredNarrativeTopics = narrativeTopics.filter((topic) => {
    const topicName = topic.name?.toLowerCase() || '';
    return topicName.includes(normalizedSearchValue);
  });

  const renderLoadingCards = (cardsToRender) => {
    const loadingCards = [];
    for (let i = 0; i < cardsToRender; i++) {
      loadingCards.push(
        <TopicLibraryCard>
          <LoaderSkeleton height={85}>
            <rect height="75" rx="7" ry="7" width="80px" x="0" y="0" />
            <rect height="10" rx="7" ry="15" width="65%" x="110" y="5" />
            <rect height="10" rx="7" ry="15" width="65%" x="110" y="25" />
          </LoaderSkeleton>
        </TopicLibraryCard>
      );
    }
    return loadingCards;
  };

  const renderTopicCards = ({ isFavorited }) => {
    return filteredNarrativeTopics
      .filter((topic) => topic.isFavorited === isFavorited)
      .map((topic) => (
        <TopicLibraryCard
          chart={getTopicLibraryCardIcon(topic.vizType)}
          key={topic.id}
          topicName={truncateTextWithEllipsis({ length: 60, text: topic.name }) || 'Untitled Topic'}
        />
      ));
  };

  return (
    <div className={styles['topics-window']}>
      <strong className={styles['topics-window-header']}>Assemble with Topic Cards</strong>
      <div className={styles['topics-window-search']}>
        <div className={styles['topics-window-search-text-area']}>
          <TextArea
            autoGrow
            className={styles['topics-window-search-control']}
            onChange={setSearchValue}
            placeholder="Find a topic"
            rows={1}
            value={searchValue}
          />
        </div>
      </div>

      {hasFavoritedTopics && <h4>Favorite Topic Cards</h4>}
      {isLoading ? renderLoadingCards(3) : renderTopicCards({ isFavorited: true })}

      <h4>{`${hasFavoritedTopics ? 'Other Topic Cards' : 'Topic Cards'}`}</h4>
      {isLoading ? renderLoadingCards(6) : renderTopicCards({ isFavorited: false })}
    </div>
  );
};

export default NarrativesTopicsWindow;
